import Typography from "@/design-system/Typography";
import Button from "@/design-system/button";
import Container from "@/design-system/container";
import { languageSelector } from "@/src/contexts";
import * as stylex from "@stylexjs/stylex";

import { colors } from "../../styles/tokens.stylex";
import Resources from "./GenericError.Resources";
import ResourcesEn from "./GenericError.Resources.En";

export interface GenericErrorProps {
    title: string;
    description: string;
    stack?: string;
}

export function GenericError({ title, description, stack }: GenericErrorProps) {
    const resources = languageSelector(Resources, ResourcesEn);

    const reset = () => window.location.reload();

    return (
        <Container Element="main" styleX={styles.container}>
            <div {...stylex.props(styles.iconContainer)}>
                <svg
                    width="120"
                    height="120"
                    viewBox="0 0 120 120"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <circle cx="60" cy="60" r="55" stroke="#DC2664" strokeWidth="4" />
                    <path d="M60 25V65" stroke="#DC2664" strokeWidth="6" strokeLinecap="round" />
                    <circle cx="60" cy="85" r="4" fill="#DC2664" />
                </svg>
            </div>

            <Typography textElement="h1" fontStyle="hM" styleX={styles.title}>
                {resources.title}
            </Typography>

            <Typography textElement="p" fontStyle="BodyL" styleX={styles.description}>
                {resources.description}
            </Typography>

            {title && (
                <div {...stylex.props(styles.errorDetails)}>
                    <Typography textElement="p" fontStyle="BodyS" styleX={styles.errorMessage}>
                        <strong>Error:</strong> {description}
                    </Typography>
                    {stack && (
                        <Typography textElement="p" fontStyle="BodyS" styleX={styles.errorDigest}>
                            <strong>Stack trace:</strong> {stack}
                        </Typography>
                    )}
                </div>
            )}

            <div {...stylex.props(styles.buttonGroup)}>
                <Button as="button" variant="magentaRound" size="L" onClick={reset}>
                    {resources.tryAgain}
                </Button>
                <Button as="a" href="/" variant="secondary" size="L">
                    {resources.goHome}
                </Button>
            </div>

            <Typography textElement="p" fontStyle="BodyM" styleX={styles.helpText}>
                {resources.helpText}{" "}
                <a href="/kontakt" {...stylex.props(styles.link)}>
                    {resources.supportTeam}
                </a>
                .
            </Typography>
        </Container>
    );
}

const styles = stylex.create({
    container: {
        gap: "1.5rem",
        alignItems: "center",
        display: "flex",
        flexDirection: "column",

        textAlign: "center",
        marginLeft: "auto",
        marginRight: "auto",
        maxWidth: "42rem",
        paddingBottom: "4rem",
        paddingTop: "4rem",
    },

    iconContainer: {
        marginBottom: "1rem",
    },

    title: {
        color: colors.fg_Primary,
        marginBottom: "0.5rem",
    },

    description: {
        color: colors.fg_Secondary,
        lineHeight: 1.6,
        maxWidth: "36rem",
    },

    errorDetails: {
        padding: "1rem",
        borderColor: colors.b_Primary,
        borderRadius: "8px",
        borderStyle: "solid",
        borderWidth: "1px",
        backgroundColor: colors.bg_Tertiary,
        textAlign: "left",
        marginTop: "1rem",
        width: "100%",
    },

    errorMessage: {
        color: colors.fg_Error,

        wordBreak: "break-word",
        marginBottom: "0.5rem",
    },

    errorDigest: {
        color: colors.fg_Secondary,
        fontFamily: "monospace",
        fontSize: "0.75rem",
    },

    buttonGroup: {
        gap: "1rem",
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "center",
        marginTop: "1rem",
    },

    helpText: {
        color: colors.fg_Tertiary,
        marginTop: "2rem",
    },

    link: {
        textDecoration: "underline",
        color: {
            default: colors.fg_BrandMagenta,
            ":hover": colors.b_HoverBrandMagenta,
        },
    },
});
